<template>
  <component
    :is="GenericSideMenu"
    position="right"
    class="EditProductDiscountModal"
    :title="$t('checkout.orderProposal.editDiscount')"
    @close="uiStore.closeAllModal()"
  >
    <MiniCartProduct
      v-for="item in items"
      :key="item.lineNo"
      :item="item"
      :hide-delete="true"
      :hide-multi-discount="true"
      :hide-cross-docking="!globalStore.getAllowBackorders || userStore.isSalesRepUser || userStore.isCustomerSuccessUser || cartStore.items.length < 2"
      :hide-meta="true"
    />
    <div class="flex flex-col">
      <p class="type-sm-medium mt-12 mb-6">{{ $t('checkout.orderProposal.editDiscount') }}</p>
      <p class="type-sm">{{ $t('checkout.addDiscountModal.description', { name: uiStore.currentlyEditingItem?.variantName}) }}</p>
    </div>
    <div class="type-sm-medium mb-24 mt-12 flex flex-col gap-8">
      <!-- Discount Type Selection -->
      <div class="flex flex-col gap-4">
        <p class="type-sm-medium">{{ $t('priceInfo.discountLabel') }}</p>
        <div class="flex gap-4">
          <div class="flex items-center">
            <input
              id="percentage-discount"
              v-model="discountType"
              type="radio"
              value="percentage"
              class="mr-8"
            >
            <label for="percentage-discount" class="type-sm ml-8">
              {{ $t('priceInfo.discountType.percentage') }}
            </label>
          </div>
          <div class="flex items-center ml-12">
            <input
              id="fixed-discount"
              v-model="discountType"
              type="radio"
              value="fixedPerM2"
              class="mr-8"
            >
            <label for="fixed-discount" class="type-sm ml-8">
              {{ globalStore.getMeasurement === 'imperial' ? $t('priceInfo.discountType.fixedPerSqft') : $t('priceInfo.discountType.fixedPerM2') }}
            </label>
          </div>
        </div>
      </div>

      <!-- Quantity and Discount Input Fields -->
      <div class="flex gap-8">
        <div class="basis-1/2">
          <TextInput
            v-model="selectDiscountQuantity"
            :type="'number'"
            :label="discountType === 'fixedPerM2' ? (globalStore.getMeasurement === 'imperial' ? 'sq. ft' : 'm²') : $t('orders.table.quanity')"
            :attr="{ 'aria-describedby': 'discount-error' }"
          />
        </div>
        <div class="basis-1/2">
          <TextInput
            v-if="discountType === 'percentage'"
            v-model="discountPercentage"
            :type="'number'"
            :label="$t('priceInfo.discountLabel') + ' %'"
            :attr="{ min: '0', max: globalStore.getMaxDiscount, 'aria-describedby': 'discount-error' }"
          />
          <TextInput
            v-if="discountType === 'fixedPerM2'"
            v-model="discountFixedPerM2"
            :type="'number'"
            :label="globalStore.getMeasurement === 'imperial' ? $t('priceInfo.fixedDiscountPerSqft') : $t('priceInfo.fixedDiscountPerM2')"
            :attr="{ min: minPricePerM2, step: '0.01', 'aria-describedby': 'discount-error' }"
          />
        </div>
      </div>

      <div
        v-if="discountType === 'percentage' && (!uiStore.currentlyEditingItem?.priceInfo.isStandardPrice || uiStore.currentlyEditingItem.priceInfoTotal.price !== uiStore.currentlyEditingItem.priceInfoTotal.priceBeforeDiscount)"
        class="flex gap-4 mt-12 basis-full"
      >
        <div class="flex items-center">
          <div class="mr-8">
            <input
              v-model="priceListLocked"
              type="radio"
              :value="false"
            >
          </div>
          <div class="type-sm-medium mr-8">
            {{ $t('fastOrder.row.user_pricelist') }}
          </div>
        </div>
        <div class="px-8 flex items-center">
          <div class="mr-8">
            <input
              v-model="priceListLocked"
              type="radio"
              :value="true"
            >
          </div>
          <div class="type-sm-medium mr-8">
            {{ $t('fastOrder.row.std_pricelist') }}
          </div>
        </div>
      </div>
    </div>
    <button
      class="btn w-full "
      :class="{ 'loading': loading }"
      :disabled="loading"
      type="button"
      @click="applyDiscount"
    >
      {{ $t('minicart.discount.activate') }}
    </button>
    <!-- Error Message -->
    <div v-if="errorMessage" id="discount-error" role="alert" class="text-criticalDark type-sm-medium leading-single block mt-12">
      {{ errorMessage }}
    </div>

    <!--    <button
      :class="{ 'disabled': loading }"
      class="btn w-full btn mb-24"
      @click="uiStore.closeAllModal()"
     type="button">
      {{ $t('search.cancel') }}
    </button>-->
  </component>
</template>

<script setup lang="ts">
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import { useUiStore } from '~/store/ui';
import { useUserStore } from '~/store/user';
import { useCartStore } from '~/store/cart';
import { useGlobalContentStore } from '~/store/globalContent';
import useApiFetch from '~/composables/useApiFetch';
import usePackageAreaConverter from '~/composables/usePackageAreaConverter';
import MiniCartProduct from '~/components/cart/MiniCartProduct.vue';
import TextInput from '~/components/form-elements/TextInput.vue';
import { formatPrice } from '~/util/priceRounding';
const { $t } = useNuxtApp();

const uiStore = useUiStore();
const userStore = useUserStore();
const cartStore = useCartStore();
const globalStore = useGlobalContentStore();

const { handleApiError } = useApiFetch();

const { convertPackagePriceToAreaPrice, unitDisplay } = usePackageAreaConverter(
  toRef(() => uiStore.currentlyEditingItem || {} as any)
);
const discountPercentage = ref<number>(0);
const discountFixedPerM2 = ref<number>(0);
const discountType = ref<'percentage' | 'fixedPerM2'>('fixedPerM2');
const items = ref([]);
const selectDiscountQuantity = ref(1);
const loading = ref(false);
const priceListLocked = ref(false);
const errorMessage = ref<string | null>(null);

const minPricePerM2 = computed(() => {
  if (!globalStore.getMaxDiscount || !uiStore.currentlyEditingItem) {
    return 0;
  }

  const originalPricePerM2 = convertPackagePriceToAreaPrice(uiStore.currentlyEditingItem.priceInfo.priceBeforeVat);

  return originalPricePerM2 ? originalPricePerM2 * (1 - globalStore.getMaxDiscount / 100) : 0;
});

onMounted(async() => {
  setItem();
});

const setItem = () => {
  items.value = [uiStore.currentlyEditingItem];
};

const applyDiscount = async() => {
  loading.value = true;
  errorMessage.value = null;
  let res = null;

  // Max percent if "percentage"
  if (discountType.value === 'percentage' && discountPercentage.value > globalStore.getMaxDiscount) {
    errorMessage.value = $t('priceInfo.maxDiscountExceeded', { maxDiscount: globalStore.getMaxDiscount });
    loading.value = false;
    return;
  }

  // Min discountPerM2
  if (discountType.value === 'fixedPerM2' && discountFixedPerM2.value < minPricePerM2.value) {
    errorMessage.value = $t('priceInfo.minPriceRequired', {
      minPrice: formatPrice(minPricePerM2.value),
      unit: unitDisplay,
    });
    loading.value = false;
    return;
  }

  // Determine discount value based on type
  const discount = discountType.value === 'percentage'
    ? discountPercentage.value
    : discountFixedPerM2.value;

  if (selectDiscountQuantity.value > 0 && uiStore.currentlyEditingItem) {
    res = await cartStore.updateProductDiscount(
      Number(uiStore.currentlyEditingItem.lineNo),
      discount.toString(),
      selectDiscountQuantity.value,
      priceListLocked.value,
      discountType.value
    );
    if (res) {
      cartStore.cart = res;
      uiStore.currentlyEditingItem = res.items?.filter(item => item.lineNo === uiStore.currentlyEditingItem.lineNo)[0];
      uiStore.closeAllModal();
    } else {
      handleApiError();
    }
  }

  loading.value = false;
};

watch([discountPercentage, discountFixedPerM2, discountType], () => {
  errorMessage.value = null;
});

watch(() => uiStore.currentlyEditingItem, () => {
  setItem();
  errorMessage.value = null;
});
</script>
